import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

// ── Tipos ────────────────────────────────────────────────────────────────

export type DiaSemana = 'SEGUNDA' | 'TERCA' | 'QUARTA' | 'QUINTA' | 'SEXTA' | 'SABADO';
export type StatusPlanoAula = 'RASCUNHO' | 'PLANEJADO' | 'EXECUTADO' | 'CANCELADO';

export interface PlanoAula {
  id: string;
  professorId: string;
  turmaId: string;
  materiaId: string;
  diaSemana: DiaSemana;
  semanaAno: number;
  anoLetivo: number;
  tema: string;
  objetivos?: string;
  conteudoPrevisto?: string;
  metodologia?: string;
  materiaisNecessarios?: string;
  status: StatusPlanoAula;
  criadoEm: string;
  atualizadoEm: string;
  turma?: { id: string; nome: string; serie?: string };
  materia?: { id: string; nome: string };
  professor?: { id: string; nome: string };
}

export interface CreatePlanoAulaInput {
  professorId: string;
  turmaId: string;
  materiaId: string;
  diaSemana: DiaSemana;
  semanaAno: number;
  anoLetivo: number;
  tema: string;
  objetivos?: string;
  conteudoPrevisto?: string;
  metodologia?: string;
  materiaisNecessarios?: string;
}

export interface UpdatePlanoAulaInput {
  tema?: string;
  objetivos?: string;
  conteudoPrevisto?: string;
  metodologia?: string;
  materiaisNecessarios?: string;
  status?: StatusPlanoAula;
}

// ── Helpers ──────────────────────────────────────────────────────────────

/** Retorna a semana do ano para uma data (ISO 8601 week number). */
export function getSemanaAno(date: Date = new Date()): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// ── Hook ─────────────────────────────────────────────────────────────────

const BASE = '/planos-aula';
const QK = 'planos-aula';

/**
 * Hook para gerenciar Planos de Aula (Fase 2).
 *
 * @param filtros - Filtros para listar planos
 */
export function usePlanoAula(filtros?: {
  professorId?: string;
  turmaId?: string;
  materiaId?: string;
  status?: StatusPlanoAula;
  semanaAno?: number;
  anoLetivo?: number;
}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QK, filtros],
    queryFn: async () => {
      const { data } = await api.get<PlanoAula[]>(BASE, { params: filtros });
      return Array.isArray(data) ? data : [];
    },
    enabled: !!(filtros?.professorId || filtros?.turmaId),
  });

  /** Lista planos para uma semana específica do professor */
  const useSemana = (professorId: string | undefined, semanaAno: number, anoLetivo: number) =>
    useQuery({
      queryKey: [QK, 'semana', professorId, semanaAno, anoLetivo],
      queryFn: async () => {
        if (!professorId) return [];
        const { data } = await api.get<PlanoAula[]>(
          `${BASE}/semana/${professorId}`,
          { params: { semanaAno, anoLetivo } },
        );
        return Array.isArray(data) ? data : [];
      },
      enabled: !!professorId,
    });

  const criar = useMutation({
    mutationFn: (input: CreatePlanoAulaInput) => api.post(BASE, input).then(r => r.data as PlanoAula),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, ...input }: UpdatePlanoAulaInput & { id: string }) =>
      api.patch(`${BASE}/${id}`, input).then(r => r.data as PlanoAula),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  const mudarStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: StatusPlanoAula }) =>
      api.patch(`${BASE}/${id}/status`, { status }).then(r => r.data as PlanoAula),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QK] }),
  });

  return {
    planos: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    criar,
    atualizar,
    mudarStatus,
    useSemana,
  };
}
